import { CheckCircle2, Copy, Mail, Printer, ShoppingCart } from 'lucide-react'
import { toast } from 'sonner'

import { useEffect, useRef } from 'react'

import { Badge, Dialog, DialogContent, DialogTitle, Kbd, PosButton } from '@/components/ui'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { useSession } from '@/contexts/SessionContext'
import { useOrderSyncState } from '@/hooks/features/orders'
import { batchLabel } from '@/hooks/features/register'
import { lineNet } from '@/services'
import { postToDisplay } from '@/services/customerDisplay'
import { usePrinter, useReceiptHeader } from '@/services/printer'
import { PosModel } from '@/types'
import { formatDate, formatDateTime, formatMoney, formatQty } from '@/utils'

import { Receipt } from './Receipt'

const SYNC_BADGE = {
  pending: { tone: 'warning', label: 'Waiting to sync' },
  synced: { tone: 'success', label: 'Synced' },
  rejected: { tone: 'error', label: 'Needs attention' },
} as const

/** The receipt as plain text, for pasting into a message. */
const receiptText = (order: PosModel.Order, shop: string) => {
  const t = order.totals
  const row = (l: string, r: string) => `${l}  ${r}`
  return [
    shop,
    row(order.number, formatDateTime(order.created_at)),
    ...order.lines.map((l) => row(`${l.product.name} ${formatQty(l.quantity)} x ${l.product.price.toFixed(2)}`, lineNet(l).toFixed(2))),
    row('Subtotal', t.subtotal.toFixed(2)),
    ...(t.lineDiscounts + t.orderDiscount > 0 ? [row('Discount', `-${(t.lineDiscounts + t.orderDiscount).toFixed(2)}`)] : []),
    row('VAT', t.tax.toFixed(2)),
    ...(t.adjustment > 0 ? [row('Adjustment', `-${t.adjustment.toFixed(2)}`)] : []),
    row('TOTAL', formatMoney(t.total)),
    ...order.payments.map((p) => row(p.method, p.amount.toFixed(2))),
    ...(order.change > 0 ? [row('Change', order.change.toFixed(2))] : []),
  ].join('\n')
}

interface ReceiptDialogProps {
  order: PosModel.Order | null
  onClose: () => void
}

/**
 * Sale done. The change owed is the biggest thing on screen; Enter starts the next sale, P prints. On opening it
 * prints (auto-print) or kicks the cash drawer, per this device's printer settings.
 */
export const ReceiptDialog = ({ order, onClose }: ReceiptDialogProps) => {
  const { printReceipt, afterSale } = usePrinter()
  const register = useRegisterContext()
  const { name: shop } = useReceiptHeader()
  const { can } = useSession()
  const syncState = useOrderSyncState(order)
  const handled = useRef<string | null>(null)

  const copy = (o: PosModel.Order) =>
    navigator.clipboard
      .writeText(receiptText(o, shop))
      .then(() => toast.success('Receipt copied'))
      .catch(() => toast.error('Could not copy the receipt'))

  useEffect(() => {
    // Once per sale (afterSale changes when the receipt header loads); after paint, so the browser print dialog
    // finds the receipt on screen.
    if (!order || handled.current === order._id) return
    handled.current = order._id
    postToDisplay({ type: 'paid', shop, currency: register.currency, total: order.totals.total, change: order.change })
    requestAnimationFrame(() => afterSale(order))
  }, [order, afterSale, shop, register.currency])

  return (
  <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      size="lg"
      className="gap-0 p-0"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onClose()
        }
        if (e.key.toLowerCase() === 'p') {
          e.preventDefault()
          if (order) printReceipt(order)
        }
      }}
    >
      {order && (
        <div className="grid grid-cols-[minmax(0,1fr)] md:grid-cols-[minmax(0,1fr)_auto]">
          <div className="flex flex-col gap-5 p-6">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="size-8 text-success" />
              <div className="min-w-0 flex-1">
                <DialogTitle>Sale complete</DialogTitle>
                <p className="text-sm text-muted-foreground">{order.number}</p>
              </div>
              {syncState && <Badge tone={SYNC_BADGE[syncState].tone}>{SYNC_BADGE[syncState].label}</Badge>}
            </div>
            <div className="rounded-md bg-success-lighter/60 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-success-dark">{order.change > 0 ? 'Change due' : 'Paid'}</p>
              {/* eslint-disable-next-line no-restricted-syntax -- the amount paid is read across a counter; larger than any token */}
              <p className="text-[2.25rem] font-semibold leading-tight tabular-nums text-success-darker">
                {formatMoney(order.change > 0 ? order.change : order.totals.total)}
              </p>
            </div>
            {!!order.stock_taken?.length && (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Stock taken</p>
                <table className="w-full text-xs">
                  <thead className="text-left text-muted-foreground">
                    <tr className="[&>th]:pb-1 [&>th]:font-medium">
                      <th>Item</th>
                      <th>Batch</th>
                      <th>Expiry</th>
                      <th className="text-right">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums text-ink">
                    {order.stock_taken.map((s, i) => (
                      <tr key={i} className="border-t border-border-subtle [&>td]:py-1">
                        <td className="max-w-40 truncate pr-2">{s.name}</td>
                        <td className="font-mono">{batchLabel(s.batch)}</td>
                        <td>{s.expiry_date ? formatDate(s.expiry_date) : '—'}</td>
                        <td className="text-right">{formatQty(s.quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {order.gross_profit !== undefined && can('products:view_cost') && (
                  <p className="text-xs text-muted-foreground">
                    Gross profit on this sale <span className="font-medium text-ink">{formatMoney(order.gross_profit)}</span>
                  </p>
                )}
              </div>
            )}
            <div className="mt-auto grid gap-2">
              <PosButton size="xl" onClick={onClose} autoFocus>
                <ShoppingCart /> New sale <Kbd dark>↵</Kbd>
              </PosButton>
              <div className="grid grid-cols-3 gap-2">
                <PosButton variant="outline" size="touch" onClick={() => printReceipt(order)}>
                  <Printer /> Print <Kbd>P</Kbd>
                </PosButton>
                <PosButton variant="outline" size="touch" onClick={() => copy(order)}>
                  <Copy /> Copy
                </PosButton>
                <PosButton
                  variant="outline"
                  size="touch"
                  disabled={!order.customer}
                  onClick={() => toast.success(`Receipt sent to ${order.customer?.phone}`)}
                >
                  <Mail /> SMS
                </PosButton>
              </div>
            </div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto border-t border-border bg-subdued p-4 md:border-l md:border-t-0">
            <div className="shadow-z8">
              <Receipt order={order} />
            </div>
          </div>
        </div>
      )}
    </DialogContent>
  </Dialog>
  )
}
